import * as React from 'react';
import { jc, jcm } from 'utils';
import { Breakpoint } from 'types/utils';
import { memo } from 'common/hocs';
import { Section } from 'features/page-navigation/types';
import { Dispatch } from 'redux';
import {
  stickyProductWrapper,
  hidden,
  invisible,
  mediumPlusProductHidden,
  mediumPlusProductVisible,
  mobileProductWrapper,
  stickyPanelInnerWrapper,
  stickyPanelMediumWrapper,
  stickyPanelTabletWrapper,
  stickyPanelWrapper,
  tabletProductWrapper,
} from './sticky-panel.styles';
import { Navigation } from './components/navigation';
import { AvailabilityState, ProductProps } from './components/types/product-props';
import {
  MediumPlusScreenProduct,
  MobileProduct,
  ProductDescription,
  ProductImage,
  TabletProduct,
} from './components/product';

export type StickyPanelProps = {
  breakpoint: Breakpoint;
  sections: Array<{
    href: string;
    title: string;
    badgeNumber?: number;
  }>;
  activeSectionHref: string;
  onSectionActivated: (section: Section) => void;
  productImageUrl: string;
  productDescription: string;
  slots?: Partial<Record<'price' | 'image' | 'description' | 'basketItem', React.ReactNode>>;
  availabilityState: AvailabilityState;
  display?: 'full' | 'only-navigation' | 'only-product' | 'none';
  dispatch?: Dispatch;
  isSet?: boolean;
  isStickyAtTop: boolean;
  quantity?: number;
  catalogue4LevelLink?: string;
} & React.HTMLAttributes<HTMLElement>;

const StickyPanelComponent: React.ForwardRefRenderFunction<HTMLDivElement, StickyPanelProps> = ({
  breakpoint,
  sections,
  activeSectionHref,
  onSectionActivated,
  productImageUrl,
  productDescription,
  slots,
  availabilityState,
  display = 'full',
  className = '',
  dispatch,
  isSet,
  quantity,
  catalogue4LevelLink,
  isStickyAtTop,
  ...htmlProps
}) => {
  const isNavigationHidden = display === 'only-product' || display === 'none';
  const isProductHidden = display === 'only-navigation' || display === 'none';

  const Image = slots?.image || (
    <ProductImage
      description={productDescription}
      onClick={() => onSectionActivated({ title: 'Товар', href: activeSectionHref })}
      url={productImageUrl}
    />
  );
  const Description = slots?.description || <ProductDescription description={productDescription} />;

  const productProps: ProductProps = {
    slots: {
      image: Image,
      description: Description,
      price: slots?.price,
      basket: slots?.basketItem,
    },
    availabilityState,
    onSectionActivated,
    quantity,
    catalogue4LevelLink,
  };

  return (
    <div
      className={jcm({
        [stickyPanelWrapper]: true,
        [stickyPanelMediumWrapper]: breakpoint === 'medium',
        [stickyPanelTabletWrapper]: breakpoint === 'tablet',
        [className]: true,
        [invisible]: display === 'none',
      })}
      {...htmlProps}
    >
      <div data-qa="sticky-panel-inner-wrapper" className={jc(stickyPanelInnerWrapper, breakpoint || '')}>
        <div
          data-qa="sticky-product-wrapper"
          className={jcm({
            [stickyProductWrapper]: true,
            [hidden]: isNavigationHidden,
          })}
        >
          <Navigation
            sections={sections}
            activeSectionHref={activeSectionHref}
            onSectionActivated={onSectionActivated}
            isSet={isSet}
          />
        </div>
        {breakpoint === 'mobile' && (
          <div className={jcm({ [mobileProductWrapper]: true, [hidden]: isProductHidden })}>
            <MobileProduct {...productProps} />
          </div>
        )}
        {breakpoint === 'tablet' && (
          <div className={jcm({ [tabletProductWrapper]: true, [hidden]: isProductHidden })}>
            <TabletProduct {...productProps} />
          </div>
        )}
        {(breakpoint === 'medium' || breakpoint === 'large') && (
          <div
            className={jcm({
              [mediumPlusProductVisible]: isStickyAtTop,
              [mediumPlusProductHidden]: !isStickyAtTop,
            })}
          >
            <MediumPlusScreenProduct {...productProps} />
          </div>
        )}
      </div>
    </div>
  );
};
export const StickyPanel = memo(StickyPanelComponent);
